import { Switch, Route, useLocation } from "wouter";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import Home from "@/pages/home";
import Header from "@/components/layout/Header";
import Promo from "@/pages/promo";
import Privacy from "@/pages/privacy";
import Tos from "@/pages/tos";
import Footer from "@/components/layout/Footer";
import { useEffect, useState } from "react";

const FallbackComponent = () => {
  return (
    <div className="error-boundary">
      <h2>Something went wrong</h2>
      <button onClick={() => window.location.reload()}>Refresh the page</button>
    </div>
  );
};

function Router() {
  const [location] = useLocation();
  const [user, setUser] = useState(null);
  const [userCredits, setUserCredits] = useState(0);
  const [isLoadingAuth, setIsLoadingAuth] = useState(false);
  
  // Apply smooth scrolling to the document
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  // Improve accessibility with keyboard navigation
  useEffect(() => {
    const handleTabKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        document.body.classList.add('user-is-tabbing');
      }
    };
    
    const handleMouseDown = () => {
      document.body.classList.remove('user-is-tabbing');
    };
    
    window.addEventListener('keydown', handleTabKeyPress);
    window.addEventListener('mousedown', handleMouseDown);
    
    return () => {
      window.removeEventListener('keydown', handleTabKeyPress);
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  return (
    <>
      {/* Skip to main content link for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:p-4 focus:bg-white focus:text-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-600"
      >
        Skip to main content
      </a>
      
      <div className="flex flex-col min-h-screen">
        <Header 
            user={user} 
            setUser={setUser} 
            setUserCredits={setUserCredits} 
            setIsLoadingAuth={setIsLoadingAuth} 
        />
        <main id="main-content" className="flex-grow">
          <Switch>
            <Route path="/">
              <Home user={user} userCredits={userCredits} />
            </Route>
            <Route path="/ro">
              <Promo 
                user={user} 
                userCredits={userCredits} 
                isLoadingAuth={isLoadingAuth} 
              />
            </Route>
            <Route path="/privacy">
              <Privacy />
            </Route>
            <Route path="/tos">
              <Tos />
            </Route>
            <Route>
              <NotFound />
            </Route>
          </Switch>
        </main>
        <Footer />
      </div>
    </>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={FallbackComponent}>
    <TooltipProvider>
      <Toaster />
      <Router />
    </TooltipProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
